// Documents API service
import { baseClient } from './baseClient';
import type {
  DocumentTemplate,
  DocumentTemplateCreate,
  DocumentTemplateUpdate,
  GeneratedProposal,
  GeneratedProposalCreate,
  GeneratedProposalUpdate,
  ProposalComment,
  ProposalCommentCreate
} from '../types/api';

export class DocumentsApi {
  private static instance: DocumentsApi;

  private constructor() {}

  public static getInstance(): DocumentsApi {
    if (!DocumentsApi.instance) {
      DocumentsApi.instance = new DocumentsApi();
    }
    return DocumentsApi.instance;
  }

  // Document Templates
  /**
   * Get all document templates
   */
  async getDocumentTemplates(): Promise<DocumentTemplate[]> {
    return baseClient.get<DocumentTemplate[]>('/document-templates/');
  }

  /**
   * Get a single document template by ID
   */
  async getDocumentTemplate(id: string): Promise<DocumentTemplate> {
    return baseClient.get<DocumentTemplate>(`/document-templates/${id}`);
  }

  /**
   * Create a new document template
   */
  async createDocumentTemplate(template: DocumentTemplateCreate): Promise<DocumentTemplate> {
    return baseClient.post<DocumentTemplate>('/document-templates/', template);
  }

  /**
   * Update an existing document template
   */
  async updateDocumentTemplate(id: string, template: DocumentTemplateUpdate): Promise<DocumentTemplate> {
    return baseClient.patch<DocumentTemplate>(`/document-templates/${id}`, template);
  }

  /**
   * Delete a document template
   */
  async deleteDocumentTemplate(id: string): Promise<void> {
    return baseClient.delete<void>(`/document-templates/${id}`);
  }

  // Generated Proposals
  /**
   * Get all generated proposals
   */
  async getGeneratedProposals(): Promise<GeneratedProposal[]> {
    return baseClient.get<GeneratedProposal[]>('/generated-proposals/');
  }

  /**
   * Get a single generated proposal by ID
   */
  async getGeneratedProposal(id: string): Promise<GeneratedProposal> {
    return baseClient.get<GeneratedProposal>(`/generated-proposals/${id}`);
  }

  /**
   * Create a new generated proposal
   */
  async createGeneratedProposal(proposal: GeneratedProposalCreate): Promise<GeneratedProposal> {
    return baseClient.post<GeneratedProposal>('/generated-proposals/', proposal);
  }

  /**
   * Update an existing generated proposal
   */
  async updateGeneratedProposal(id: string, proposal: GeneratedProposalUpdate): Promise<GeneratedProposal> {
    return baseClient.patch<GeneratedProposal>(`/generated-proposals/${id}`, proposal);
  }

  /**
   * Delete a generated proposal
   */
  async deleteGeneratedProposal(id: string): Promise<void> {
    return baseClient.delete<void>(`/generated-proposals/${id}`);
  }

  /**
   * Get proposals by deal
   */
  async getProposalsByDeal(dealId: string): Promise<GeneratedProposal[]> {
    return baseClient.get<GeneratedProposal[]>(`/generated-proposals/deals/${dealId}`);
  }

  /**
   * Get proposal by share token
   */
  async getProposalByShareToken(token: string): Promise<GeneratedProposal> {
    return baseClient.get<GeneratedProposal>(`/generated-proposals/share/${token}`);
  }

  /**
   * Generate proposal from template
   */
  async generateProposal(dealId: string, templateId: string): Promise<GeneratedProposal> {
    return baseClient.post<GeneratedProposal>('/generated-proposals/generate', {
      deal_id: dealId,
      template_id: templateId
    });
  }

  /**
   * Send proposal to customer
   */
  async sendProposal(id: string, recipients: string[]): Promise<void> {
    return baseClient.post<void>(`/generated-proposals/${id}/send`, { recipients });
  }

  /**
   * Track proposal view
   */
  async trackProposalView(token: string): Promise<void> {
    return baseClient.post<void>(`/generated-proposals/track-view/${token}`);
  }

  // Proposal Comments
  /**
   * Get comments for a proposal
   */
  async getProposalComments(proposalId: string): Promise<ProposalComment[]> {
    return baseClient.get<ProposalComment[]>(`/generated-proposals/${proposalId}/comments`);
  }

  /**
   * Add comment to proposal
   */
  async addProposalComment(comment: ProposalCommentCreate): Promise<ProposalComment> {
    return baseClient.post<ProposalComment>('/proposal-comments/', comment);
  }

  /**
   * Update proposal comment
   */
  async updateProposalComment(id: string, comment: Partial<ProposalCommentCreate>): Promise<ProposalComment> {
    return baseClient.patch<ProposalComment>(`/proposal-comments/${id}`, comment);
  }

  /**
   * Delete proposal comment
   */
  async deleteProposalComment(id: string): Promise<void> {
    return baseClient.delete<void>(`/proposal-comments/${id}`);
  }

  // File Upload
  /**
   * Upload document file
   */
  async uploadDocument(file: File, dealId?: string): Promise<any> {
    return baseClient.upload('/documents/upload', file, dealId ? { deal_id: dealId } : undefined);
  }

  /**
   * Get documents by deal
   */
  async getDocumentsByDeal(dealId: string): Promise<any[]> {
    return baseClient.get<any[]>(`/documents/deals/${dealId}`);
  }

  /**
   * Get all documents
   */
  async getDocuments(): Promise<any[]> {
    return baseClient.get<any[]>('/documents/');
  }

  /**
   * Delete document
   */
  async deleteDocument(id: string): Promise<void> {
    return baseClient.delete<void>(`/documents/${id}`);
  }

  // Document Management
  /**
   * Get document categories
   */
  async getDocumentCategories(): Promise<string[]> {
    return baseClient.get<string[]>('/documents/categories');
  }

  /**
   * Get documents by category
   */
  async getDocumentsByCategory(category: string): Promise<any[]> {
    return baseClient.get<any[]>(`/documents/categories/${category}`);
  }

  /**
   * Search documents
   */
  async searchDocuments(query: string): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('q', query);
    
    return baseClient.get<any[]>(`/documents/search/?${params.toString()}`);
  }

  /**
   * Get document statistics
   */
  async getDocumentStats(): Promise<any> {
    return baseClient.get<any>('/documents/stats');
  }

  /**
   * Get recent documents
   */
  async getRecentDocuments(limit: number = 10): Promise<any[]> {
    return baseClient.get<any[]>(`/documents/recent?limit=${limit}`);
  }

  /**
   * Get document versions
   */
  async getDocumentVersions(documentId: string): Promise<any[]> {
    return baseClient.get<any[]>(`/documents/${documentId}/versions`);
  }

  /**
   * Restore document version
   */
  async restoreDocumentVersion(documentId: string, versionId: string): Promise<void> {
    return baseClient.post<void>(`/documents/${documentId}/versions/${versionId}/restore`);
  }

  /**
   * Share document
   */
  async shareDocument(documentId: string, recipients: string[], permissions: string[]): Promise<any> {
    return baseClient.post<any>(`/documents/${documentId}/share`, {
      recipients,
      permissions
    });
  }

  /**
   * Get shared documents
   */
  async getSharedDocuments(): Promise<any[]> {
    return baseClient.get<any[]>('/documents/shared');
  }

  /**
   * Export document
   */
  async exportDocument(documentId: string, format: 'pdf' | 'docx' | 'html' = 'pdf'): Promise<Blob> {
    const response = await baseClient.get<Blob>(`/documents/${documentId}/export?format=${format}`, {
      responseType: 'blob'
    });
    return response;
  }

  /**
   * Get document preview
   */
  async getDocumentPreview(documentId: string): Promise<string> {
    return baseClient.get<string>(`/documents/${documentId}/preview`);
  }
}

// Export singleton instance
export const documentsApi = DocumentsApi.getInstance(); 